import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getProjectsWithPagination, toggleFeaturedProject, deleteProject } from "../api/projects";
import { IProject, IProjectsResponse, IQueryParams } from "../types/project";
import {
	Box,
	Button,
	Typography,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Paper,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Alert,
	CircularProgress,
	Chip,
	TableContainer,
	Tooltip,
	Fab,
	Snackbar,
	Pagination,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';


export default function ProjectsPage() {
	const [deleteId, setDeleteId] = useState<string | null>(null);
	const [deleteProjectName, setDeleteProjectName] = useState<string>("");
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
		open: false,
		message: "",
		severity: "success"
	});
	const [pagination, setPagination] = useState({
		page: 1,
		limit: 10,
	});
	const [filters, setFilters] = useState<IQueryParams>({});
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	// Fetch projects with React Query and pagination
	const { data: projectsData, isLoading, error } = useQuery({
		queryKey: ["projects", pagination.page, pagination.limit, filters],
		queryFn: () => getProjectsWithPagination({
			...filters,
			page: pagination.page,
			limit: pagination.limit,
		}),
	});

	const projects = projectsData?.projects || [];
	const paginationInfo = projectsData?.pagination;

	// Toggle featured mutation
	const toggleFeaturedMutation = useMutation({
		mutationFn: ({ id, featured }: { id: string; featured: boolean }) =>
			toggleFeaturedProject(id, featured),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["projects"] });
			setSnackbar({
				open: true,
				message: "Featured status updated successfully",
				severity: "success"
			});
		},
		onError: () => {
			setSnackbar({
				open: true,
				message: "Failed to update featured status",
				severity: "error"
			});
		}
	});

	// Delete project mutation
	const deleteMutation = useMutation({
		mutationFn: deleteProject,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["projects"] });
			setDeleteId(null);
			setSnackbar({
				open: true,
				message: "Project deleted successfully",
				severity: "success"
			});
		},
		onError: () => {
			setSnackbar({
				open: true,
				message: "Failed to delete project",
				severity: "error"
			});
		}
	});

	const handleToggleFeatured = (id: string, currentValue: boolean) => {
		toggleFeaturedMutation.mutate({ id, featured: !currentValue });
	};

	const handleDeleteClick = (project: IProject) => {
		setDeleteId(project._id);
		setDeleteProjectName(project.title.en);
	};

	const handleConfirmDelete = () => {
		if (!deleteId) return;
		deleteMutation.mutate(deleteId);
	};

	const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
		setPagination(prev => ({ ...prev, page }));
	};

	const handleLimitChange = (event: any) => {
		const newLimit = event.target.value;
		setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
	};

	const handleFilterChange = (filterType: keyof IQueryParams, value: any) => {
		setFilters(prev => ({ ...prev, [filterType]: value }));
		setPagination(prev => ({ ...prev, page: 1 }));
	};

	const getCategoryColor = (category: string) => {
		const colors: Record<string, "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"> = {
			frontend: "primary",
			backend: "secondary",
			fullstack: "success",
			mobile: "info"
		};
		return colors[category] || "default";
	};

	const formatDate = (dateString: string) => {
		try {
			const date = new Date(dateString);
			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric'
			});
		} catch {
			return "N/A";
		}
	};

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Box p={3}>
				<Alert severity="error">
					Failed to load projects. Please try again later.
				</Alert>
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3 }}>
			{/* Header */}
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
				<Box>
					<Typography variant="h4" component="h1" gutterBottom>
						Projects Management
					</Typography>
					<Typography variant="body1" color="text.secondary">
						Manage your portfolio projects
					</Typography>
				</Box>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={() => navigate("/dashboard/projects/create")}
					size="large"
				>
					Add Project
				</Button>
			</Box>

			{/* Filters */}
			<Paper elevation={1} sx={{ p: 2, mb: 3 }}>
				<Box
					display="flex"
					flexWrap="wrap"
					gap={2}
					alignItems="center"
					sx={{
						'& > *': {
							minWidth: { xs: '100%', sm: '200px', md: '250px' },
							flex: { xs: '1 1 100%', sm: '1 1 200px', md: '1 1 250px' }
						}
					}}
				>
					<FormControl fullWidth size="small">
						<InputLabel>Category</InputLabel>
						<Select
							value={filters.category || ""}
							onChange={(e) => handleFilterChange('category', e.target.value || undefined)}
							label="Category"
						>
							<MenuItem value="">All Categories</MenuItem>
							<MenuItem value="frontend">Frontend</MenuItem>
							<MenuItem value="backend">Backend</MenuItem>
							<MenuItem value="fullstack">Fullstack</MenuItem>
							<MenuItem value="mobile">Mobile</MenuItem>
						</Select>
					</FormControl>

					<FormControl fullWidth size="small">
						<InputLabel>Featured</InputLabel>
						<Select
							value={filters.featured === undefined ? "" : filters.featured.toString()}
							onChange={(e) => handleFilterChange('featured', e.target.value === "" ? undefined : e.target.value === "true")}
							label="Featured"
						>
							<MenuItem value="">All Projects</MenuItem>
							<MenuItem value="true">Featured Only</MenuItem>
							<MenuItem value="false">Not Featured</MenuItem>
						</Select>
					</FormControl>

					<FormControl fullWidth size="small">
						<InputLabel>Items per page</InputLabel>
						<Select
							value={pagination.limit}
							onChange={handleLimitChange}
							label="Items per page"
						>
							<MenuItem value={5}>5</MenuItem>
							<MenuItem value={10}>10</MenuItem>
							<MenuItem value={25}>25</MenuItem>
							<MenuItem value={50}>50</MenuItem>
							<MenuItem value={100}>100</MenuItem>
						</Select>
					</FormControl>

					<Box display="flex" gap={1}>
						<Button
							variant="outlined"
							onClick={() => {
								setFilters({});
								setPagination({ page: 1, limit: 10 });
							}}
							size="small"
						>
							Clear Filters
						</Button>
					</Box>
				</Box>
			</Paper>

			{/* Projects Table */}
			<TableContainer component={Paper} elevation={2}>
				<Table>
					<TableHead>
						<TableRow sx={{ backgroundColor: "primary.main" }}>
							<TableCell sx={{ color: "white", fontWeight: "bold" }}>Project</TableCell>
							<TableCell sx={{ color: "white", fontWeight: "bold" }}>Category</TableCell>
							<TableCell sx={{ color: "white", fontWeight: "bold" }}>Technologies</TableCell>
							<TableCell sx={{ color: "white", fontWeight: "bold" }}>Featured</TableCell>
							<TableCell sx={{ color: "white", fontWeight: "bold" }}>Created</TableCell>
							<TableCell sx={{ color: "white", fontWeight: "bold" }} align="right">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{projects.length === 0 ? (
							<TableRow>
								<TableCell colSpan={6} align="center" sx={{ py: 4 }}>
									<Typography variant="body1" color="text.secondary">
										No projects found. Create your first project!
									</Typography>
								</TableCell>
							</TableRow>
						) : (
							projects.map((project) => (
								<TableRow key={project._id} hover>
									<TableCell>
										<Box>
											<Typography variant="subtitle1" fontWeight="medium">
												{project.title.en}
											</Typography>
											<Typography variant="body2" color="text.secondary">
												{project.title.ru}
											</Typography>
											{project.description?.en && (
												<Typography variant="caption" color="text.secondary" sx={{
													display: "-webkit-box",
													WebkitLineClamp: 2,
													WebkitBoxOrient: "vertical",
													overflow: "hidden"
												}}>
													{project.description.en}
												</Typography>
											)}
										</Box>
									</TableCell>
									<TableCell>
										<Chip
											label={project.category}
											color={getCategoryColor(project.category)}
											size="small"
											variant="outlined"
										/>
									</TableCell>
									<TableCell>
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
											{project.technologies?.slice(0, 3).map((tech, index) => (
												<Chip
													key={index}
													label={tech}
													size="small"
													variant="outlined"
													sx={{ fontSize: "0.7rem" }}
												/>
											))}
											{project.technologies && project.technologies.length > 3 && (
												<Chip
													label={`+${project.technologies.length - 3}`}
													size="small"
													variant="outlined"
													sx={{ fontSize: "0.7rem" }}
												/>
											)}
										</Box>
									</TableCell>
									<TableCell>
										<Switch
											checked={project.featured ?? false}
											onChange={() => handleToggleFeatured(project._id, project.featured ?? false)}
											disabled={toggleFeaturedMutation.isPending}
										/>
									</TableCell>
									<TableCell>
										<Typography variant="body2" color="text.secondary">
											{formatDate(project.createdAt || "")}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
											<Tooltip title="View Project">
												<IconButton
													size="small"
													onClick={() => window.open(project.demoUrl, '_blank')}
													disabled={!project.demoUrl}
												>
													<VisibilityIcon />
												</IconButton>
											</Tooltip>
											<Tooltip title="Edit Project">
												<IconButton
													size="small"
													onClick={() => navigate(`/dashboard/projects/edit/${project._id}`)}
												>
													<EditIcon />
												</IconButton>
											</Tooltip>
											<Tooltip title="Delete Project">
												<IconButton
													size="small"
													color="error"
													onClick={() => handleDeleteClick(project)}
												>
													<DeleteIcon />
												</IconButton>
											</Tooltip>
										</Box>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Pagination */}
			{paginationInfo && paginationInfo.pages > 1 && (
				<Box display="flex" justifyContent="center" mt={3}>
					<Pagination
						count={paginationInfo.pages}
						page={paginationInfo.page}
						onChange={handlePageChange}
						color="primary"
						size="large"
						showFirstButton
						showLastButton
					/>
				</Box>
			)}

			{/* Pagination Info */}
			{paginationInfo && (
				<Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
					<Typography variant="body2" color="text.secondary">
						Showing {((paginationInfo.page - 1) * paginationInfo.limit) + 1} to{' '}
						{Math.min(paginationInfo.page * paginationInfo.limit, paginationInfo.total)} of{' '}
						{paginationInfo.total} projects
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Page {paginationInfo.page} of {paginationInfo.pages}
					</Typography>
				</Box>
			)}

			{/* Delete Confirmation Dialog */}
			<Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="sm" fullWidth>
				<DialogTitle>Confirm Deletion</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete the project "{deleteProjectName}"?
						This action cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteId(null)} disabled={deleteMutation.isPending}>
						Cancel
					</Button>
					<Button
						onClick={handleConfirmDelete}
						color="error"
						disabled={deleteMutation.isPending}
						startIcon={deleteMutation.isPending ? <CircularProgress size={16} /> : null}
					>
						{deleteMutation.isPending ? "Deleting..." : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Success/Error Snackbar */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity}
					sx={{ width: '100%' }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>

			{/* Floating Action Button for Mobile */}
			<Fab
				color="primary"
				aria-label="add project"
				sx={{
					position: 'fixed',
					bottom: 16,
					right: 16,
					display: { xs: 'flex', md: 'none' }
				}}
				onClick={() => navigate("/dashboard/projects/create")}
			>
				<AddIcon />
			</Fab>
		</Box>
	);
}